import React, { useEffect, useState, useRef } from 'react';
import { SearchIcon, XIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useUserRole } from '../../context/UserRoleContext';
import { usePlatform } from '../../context/PlatformContext';
export interface Suggestion {
  id: string;
  label: string;
  sublabel?: string;
  type?: string;
  image?: string;
  /** Optional bucket name. Consecutive suggestions sharing a group render under a sticky group heading. */
  group?: string;
}
interface SearchWithAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect?: (suggestion: Suggestion) => void;
  onSearch?: (query: string) => void;
  suggestions: Suggestion[];
  placeholder?: string;
  className?: string;
  variant?: 'default' | 'rounded' | 'pill';
  showIcon?: boolean;
  mobileFullscreen?: boolean;
  autoFocus?: boolean;
  disabled?: boolean;
}
export const SearchWithAutocomplete: React.FC<SearchWithAutocompleteProps> = ({
  value,
  onChange,
  onSelect,
  onSearch,
  suggestions,
  placeholder = 'Search...',
  className = '',
  variant = 'default',
  showIcon = true,
  mobileFullscreen = false,
  autoFocus = false,
  disabled = false
}) => {
  const dk = useDarkClasses();
  const {
    isReader
  } = useUserRole();
  const {
    isAfterTide
  } = usePlatform();
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Filter suggestions based on input value if not handled by parent
  // Note: In a real app, filtering might happen server-side or in parent
  const filteredSuggestions = suggestions.filter(item => item.label.toLowerCase().includes(value.toLowerCase()));
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  useEffect(() => {
    if (value && filteredSuggestions.length > 0) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
    setHighlightedIndex(-1);
  }, [value, filteredSuggestions.length]);
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex(prev => prev < filteredSuggestions.length - 1 ? prev + 1 : prev);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightedIndex >= 0 && filteredSuggestions[highlightedIndex]) {
        handleSelect(filteredSuggestions[highlightedIndex]);
      } else if (onSearch) {
        onSearch(value);
        setIsOpen(false);
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  };
  const handleSelect = (suggestion: Suggestion) => {
    if (onSelect) {
      onSelect(suggestion);
    } else {
      onChange(suggestion.label);
    }
    setIsOpen(false);
  };
  const getVariantClasses = () => {
    if (mobileFullscreen) return 'rounded-none border-0 border-b';
    switch (variant) {
      case 'rounded':
        return 'rounded-md';
      case 'pill':
        return 'rounded-full';
      default:
        return 'rounded-md';
    }
  };
  return <div ref={wrapperRef} className={`relative w-full ${className}`} data-id="element-3898" data-mp="fm88vh4git5s">
      <div className="relative" data-id="element-3899" data-mp="fo1klatxg3kn">
        {showIcon && <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3900" data-mp="2ey1c3vhu8uqy">
            <SearchIcon className={`h-5 w-5 ${isAfterTide ? 'text-[#a0a0b0]' : dk.isDark ? 'text-gray-400' : 'text-gray-400'}`} data-id="element-3901" data-mp="2a1161kkznrzu" />
          </div>}
        <input ref={inputRef} type="text" className={`block w-full ${showIcon ? 'pl-10' : 'pl-4'} pr-10 py-2 border ${mobileFullscreen ? isAfterTide ? 'bg-[#1a1a2e] border-[#16213e] text-[#f5f0e8] placeholder-[#a0a0b0] py-4 text-base' : dk.isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 py-4 text-base' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400 py-4 text-base' : isAfterTide ? `bg-[#16213e] border-[#1a1a2e] text-[#f5f0e8] placeholder-[#a0a0b0] focus:ring-[#e2b04a] focus:border-[#e2b04a]` : dk.isDark ? `bg-gray-700 border-gray-600 text-white placeholder-gray-400 ${isReader ? 'focus:ring-indigo-500 focus:border-indigo-500' : 'focus:ring-purple-500 focus:border-purple-500'}` : `bg-white border-gray-300 text-gray-900 placeholder-gray-500 ${isReader ? 'focus:ring-indigo-500 focus:border-indigo-500' : 'focus:ring-purple-500 focus:border-purple-500'}`} ${getVariantClasses()} focus:outline-none ${!mobileFullscreen && 'focus:ring-2'} sm:text-sm transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`} placeholder={placeholder} value={value} onChange={e => !disabled && onChange(e.target.value)} onFocus={() => {
        if (!disabled && value && filteredSuggestions.length > 0) setIsOpen(true);
      }} onKeyDown={!disabled ? handleKeyDown : undefined} disabled={disabled} data-id="element-3902" data-mp="1boorhav7xrns" />
        {value && <button onClick={() => {
        onChange('');
        setIsOpen(false);
        inputRef.current?.focus();
      }} className={`absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500 ${mobileFullscreen ? 'touch-target' : ''}`} data-id="element-3903" data-mp="j1p0q4vqvxvp">
            <XIcon className="h-4 w-4" data-id="element-3904" data-mp="o1kxpk60yp1r" />
          </button>}
      </div>

      {(isOpen || mobileFullscreen) && filteredSuggestions.length > 0 && <div className={`${mobileFullscreen ? `w-full bg-transparent mt-0` : `absolute z-50 mt-1 w-full shadow-lg max-h-60 rounded-md py-1 ring-1 ring-black ring-opacity-5 overflow-auto ${isAfterTide ? 'bg-[#1a1a2e] border border-[#16213e]' : dk.isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}`} data-id="element-3905" data-mp="v257e5yk4jqb">
          {filteredSuggestions.map((suggestion, index) => {
        const previousGroup = index > 0 ? filteredSuggestions[index - 1].group : undefined;
        const showGroupHeading = !!suggestion.group && suggestion.group !== previousGroup;
        return <React.Fragment key={suggestion.id} data-id="element-3906" data-mp="1sqp06ar7pio6" data-mpi={index}>
                {showGroupHeading && <div role="presentation" className={`sticky top-0 z-10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wide ${index > 0 ? 'border-t' : ''} ${isAfterTide ? 'bg-[#16213e] text-[#a0a0b0] border-[#1a1a2e]' : dk.isDark ? 'bg-gray-900/95 text-gray-400 border-gray-700' : 'bg-gray-50 text-gray-500 border-gray-200'}`} data-id="element-3907" data-mp="3pl21qwnpt1wt">
                    {suggestion.group}
                  </div>}
                <div className={`cursor-pointer select-none relative flex items-center ${mobileFullscreen ? 'py-3 px-4 border-b border-gray-100 dark:border-gray-800 touch-target-lg' : 'py-2 pl-3 pr-9'} ${index === highlightedIndex ? isAfterTide ? 'bg-[#16213e] text-[#e2b04a]' : dk.isDark ? 'bg-gray-700 text-white' : isReader ? 'bg-indigo-50 text-indigo-900' : 'bg-purple-50 text-purple-900' : isAfterTide ? 'text-[#f5f0e8] hover:bg-[#16213e]' : dk.isDark ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-900 hover:bg-gray-50'}`} onClick={() => handleSelect(suggestion)} onMouseEnter={() => setHighlightedIndex(index)} data-id="element-3908" data-mp="1xx402s2kbgkq">
                  {suggestion.image && <img src={suggestion.image} alt="" className={`${mobileFullscreen ? 'h-10 w-10' : 'h-8 w-8'} rounded-md object-cover mr-3 flex-shrink-0`} data-id="element-3909" data-mp="359czc08bfbsw" />}
                  <div className="flex flex-col min-w-0" data-id="element-3910" data-mp="30ajoygobs0wy">
                    <span className={`block truncate ${index === highlightedIndex ? 'font-semibold' : 'font-normal'} ${mobileFullscreen ? 'text-base' : 'text-sm'}`} data-id="element-3911" data-mp="30d5vqe3e6ce4">
                      {suggestion.label}
                    </span>
                    {suggestion.sublabel && <span className={`block truncate text-xs ${isAfterTide ? 'text-[#a0a0b0]' : dk.isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-3912" data-mp="3j9nv9hohfgkx">
                        {suggestion.sublabel}
                      </span>}
                  </div>
                  {suggestion.type && <span className={`ml-auto text-xs px-2 py-0.5 ${isAfterTide ? 'rounded-sm bg-[#16213e] text-[#a0a0b0]' : dk.isDark ? 'rounded-full bg-gray-600 text-gray-300' : 'rounded-full bg-gray-100 text-gray-600'}`} data-id="element-3913" data-mp="209oise6un66h">
                      {suggestion.type}
                    </span>}
                </div>
              </React.Fragment>;
      })}
        </div>}
    </div>;
};